import React from 'react';
import { ArrowRight, Heart, Trophy, Users, MapPin, Check } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, SQUASH, PROGRAMMES } from '../mock';

const Community = () => {
  useScrollReveal();
  useSeo({ title: 'LRG Squash and the LRG Squash Foundation', description: 'Youth development through sport: LRG Squash academies in Baltimore and Princeton, squash and soccer programmes across Botswana, coach development and college recruitment consulting.', path: '/community', image: '/images/community-soccer-botswana.jpg' });

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[82vh] min-h-[520px] flex items-end overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.communitySoccerBotswana} alt="Children at an LRG Squash Foundation soccer session in Botswana" className="w-full h-full object-cover object-[center_62%] kenburns" data-testid="community-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071627] via-[#071627]/35 to-[#071627]/20" />
        </div>
        <div className="relative z-10 container-lg pb-16">
          <p className="eyebrow-light mb-4 reveal is-visible">Community</p>
          <h1 className="font-serif text-[#f7f4ee] text-5xl md:text-7xl font-medium max-w-4xl leading-[1.05]">LRG Squash and the Foundation</h1>
          <div className="gold-rule mt-8" />
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#f7f4ee]">
        <div className="container-lg grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 reveal img-zoom">
            <img src={IMAGES.botswanaJuniors} alt="Botswana junior squash players and coaches travelling to an international tournament" className="w-full object-cover" />
          </div>
          <div className="lg:col-span-6 reveal">
            <p className="eyebrow mb-4">Our Mission</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0a1f38] leading-tight mb-8">Building character through sport.</h2>
            <div className="space-y-6 text-[#3a3f47] font-light leading-relaxed text-lg">
              <p>The LRG Squash Foundation is dedicated to fostering youth development through sport. From squash courts in the United States to squash and soccer programmes across Botswana, we use sport to instil life skills, promote physical fitness, and pave the way for a brighter future, developing athletes and the coaches who guide them.</p>
              <p>Our mission is to provide access to opportunities that enable young individuals to discover their passions, develop essential life skills, and build a healthy mind and body, including pathways to leading universities across the United States.</p>
            </div>
            <a href="https://www.lrgsquashfoundation.org/" target="_blank" rel="noopener noreferrer" className="btn-outline mt-10">Visit the Foundation <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      {/* Two continents */}
      <section className="py-28 bg-[#efe9df]" data-testid="programmes-section">
        <div className="container-lg">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-5">Two Continents / One Mission</p>
              <h2 className="font-serif text-4xl md:text-6xl text-[#0a1f38] leading-[1.02]">Developing athletes <span className="italic text-[#a3813f]">and the coaches behind them.</span></h2>
            </div>
            <p className="lg:col-span-5 text-[#3a3f47] font-light leading-relaxed text-lg">Squash in the United States. Squash and soccer in Botswana. One philosophy: sport as the surest route to discipline, education and opportunity.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {PROGRAMMES.map((p, i) => (
              <article key={p.region} className="bg-white shadow-sm reveal" style={{ transitionDelay: `${i * 120}ms` }} data-testid={`programme-${i}`}>
                <div className="img-zoom h-80"><img src={IMAGES[p.image]} alt={p.caption} className="w-full h-full object-cover object-[center_40%]" /></div>
                <div className="p-10">
                  <p className="eyebrow mb-3">0{i + 1} / {p.region}</p>
                  <h3 className="font-serif text-3xl text-[#0a1f38] mb-5">{p.title}</h3>
                  <p className="text-[#3a3f47] font-light leading-relaxed mb-6">{p.text}</p>
                  <ul className="grid sm:grid-cols-2 gap-2">{p.points.map((x) => <li key={x} className="flex gap-2 text-sm text-[#6b6f76] font-light"><Check size={14} className="text-[#c6a15b] shrink-0 mt-0.5" />{x}</li>)}</ul>
                  <p className="text-[#6b6f76] text-xs font-light mt-6 italic">{p.caption}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Recognition: BDF Commander */}
      <section className="py-28 bg-[#060d18]" data-testid="ambassador-recognition-section">
        <div className="container-lg grid lg:grid-cols-12 gap-14 items-center">
          <figure className="lg:col-span-5 reveal">
            <div className="border border-[#c6a15b]/30 p-3"><div className="img-zoom"><img src={IMAGES.bdfCommander} alt="Lefika Ragontse with His Excellency Mpho Churchill Orebotse Mophuting, then Botswana’s Ambassador to the United States" className="w-full object-cover" /></div></div>
            <figcaption className="text-white/50 text-sm font-light mt-4 leading-relaxed">Lefika Ragontse with His Excellency Mpho Churchill Orebotse Mophuting, then Botswana&rsquo;s Ambassador to the United States.</figcaption>
          </figure>
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: '120ms' }}>
            <p className="eyebrow eyebrow-line eyebrow-light mb-8">National Recognition</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f7f4ee] leading-[1.02]">Recognised by Botswana&rsquo;s then <span className="italic text-[#d9bd82]">Ambassador to the United States.</span></h2>
            <div className="space-y-6 text-white/70 font-light leading-relaxed text-lg mt-10">
              <p>During his tenure as Botswana&rsquo;s Ambassador to the United States, His Excellency Mpho Churchill Orebotse Mophuting congratulated LRG Holdings on its commitment to supporting and empowering Botswana&rsquo;s youth through sport, education and international opportunity.</p>
              <p>General Mophuting subsequently became Commander of the Botswana Defence Force on 3 November 2025. His recognition of LRG Holdings was made during his earlier diplomatic service as Botswana&rsquo;s Ambassador to the United States.</p>
              <p>The recognition reflects a shared conviction between LRG Holdings and the nation&rsquo;s institutions: that the character forged through sport builds the citizens and leaders Botswana needs.</p>
            </div>
            <div className="grid grid-cols-3 ledger-grid dark mt-12">
              {[['Ambassador', 'To the United States'], ['Washington', 'Embassy of Botswana'], ['Youth', 'Empowerment recognised']].map(([v, l]) => (
                <div key={l} className="p-5"><p className="font-serif text-2xl md:text-3xl text-[#f7f4ee]">{v}</p><p className="text-white/45 text-[10px] tracking-[0.18em] uppercase mt-1">{l}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact pillars */}
      <section className="bg-[#0a1f38] py-24">
        <div className="container-lg">
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { icon: Heart, t: 'Life Skills', s: 'Discipline, resilience, and teamwork cultivated on and off the court.' },
              { icon: Trophy, t: 'Athletic Advancement', s: 'Pathways to compete and study at Ivy League and NESCAC institutions.' },
              { icon: Users, t: 'Coach Development', s: 'Training and certifying local coaches so programmes are led by Batswana and endure.' },
            ].map((c, i) => (
              <div key={i} className="reveal text-center" style={{ transitionDelay: `${i * 100}ms` }}>
                <c.icon className="mx-auto text-[#c6a15b] mb-6" size={38} strokeWidth={1.2} />
                <h3 className="font-serif text-2xl text-[#f7f4ee] mb-3">{c.t}</h3>
                <p className="text-white/60 font-light leading-relaxed">{c.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LRG Squash */}
      <section className="py-28 bg-[#f7f4ee]" data-testid="lrg-squash-section">
        <div className="container-lg">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-5">01 / LRG Squash</p>
              <h2 className="font-serif text-4xl md:text-6xl text-[#0a1f38] leading-[1.02]">Get in the game with LRG Squash.</h2>
            </div>
            <p className="lg:col-span-5 text-[#3a3f47] font-light leading-relaxed text-lg">{SQUASH.intro}</p>
          </div>
          <div className="grid md:grid-cols-2 ledger-grid mb-16">
            {SQUASH.locations.map((l, i) => (
              <a key={l.city} href={l.link} target="_blank" rel="noopener noreferrer" className="p-10 group reveal" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`squash-location-${i}`}>
                <div className="flex items-center justify-between"><span className="numeral text-3xl">0{i + 1}</span><MapPin className="text-[#c6a15b]" size={22} strokeWidth={1.4} /></div>
                <h3 className="font-serif text-4xl text-[#0a1f38] mt-8 mb-5">{l.city}</h3>
                <ul className="flex flex-wrap gap-2">{l.items.map((x) => <li key={x} className="text-[11px] tracking-[0.14em] uppercase border border-[#0a1f38]/25 px-3 py-1.5 text-[#0a1f38]">{x}</li>)}</ul>
                <span className="inline-flex items-center gap-2 mt-8 text-[#a3813f] tracking-[0.18em] uppercase text-xs font-semibold">Explore Programs <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
              </a>
            ))}
          </div>
          <p className="eyebrow mb-6">Squash Academy</p>
          <div className="grid md:grid-cols-3 ledger-grid">
            {SQUASH.academy.map((a, i) => (
              <div key={a.level} className="p-10 reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <span className="numeral text-2xl">0{i + 1}</span>
                <h3 className="font-serif text-3xl text-[#0a1f38] mt-6 mb-3">{a.level}</h3>
                <p className="text-[#6b6f76] font-light leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches + College consulting */}
      <section className="py-28 bg-[#0a1f38]" data-testid="squash-coaches-section">
        <div className="container-lg">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
            <div className="lg:col-span-7">
              <p className="eyebrow eyebrow-light mb-5">02 / Coaching and College Recruitment</p>
              <h2 className="font-serif text-4xl md:text-6xl text-[#f7f4ee] leading-[1.02]">Champions coaching the next generation.</h2>
            </div>
            <p className="lg:col-span-5 text-white/60 font-light leading-relaxed text-lg">{SQUASH.consulting}</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {SQUASH.coaches.map((c, i) => (
              <div key={c.name} className="glass-card p-8 reveal" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`squash-coach-${i}`}>
                <div className="w-14 h-14 rounded-full border border-[#c6a15b]/50 flex items-center justify-center font-serif text-xl text-[#d9bd82]">{c.name.split(' ').map((n) => n[0]).join('')}</div>
                <h3 className="font-serif text-2xl text-[#f7f4ee] mt-6">{c.name}</h3>
                <p className="text-[#d9bd82] text-[11px] tracking-[0.18em] uppercase mt-1 mb-5">{c.role}</p>
                <ul className="space-y-2">{c.points.map((p) => <li key={p} className="flex gap-3 text-white/60 font-light text-sm leading-snug"><Check size={14} className="text-[#c6a15b] shrink-0 mt-0.5" />{p}</li>)}</ul>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-14 reveal">
            <a href={SQUASH.site} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="squash-site-link">Visit LRG Squash <ArrowRight size={15} /></a>
            <a href={`mailto:${SQUASH.email}?subject=College%20Consulting`} className="btn-outline btn-outline-light">College Consulting Enquiry</a>
          </div>
        </div>
      </section>

      {/* CTA image */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.communitySquashUsa} alt="LRG Squash juniors on court" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#071627]/80" />
        </div>
        <div className="relative z-10 container-lg text-center max-w-2xl mx-auto reveal">
          <h2 className="font-serif text-4xl md:text-5xl text-[#f7f4ee] leading-tight">Support the next generation of Botswana champions.</h2>
          <a href="https://www.lrgsquashfoundation.org/" target="_blank" rel="noopener noreferrer" className="btn-gold mt-10">Learn More <ArrowRight size={16} /></a>
        </div>
      </section>
    </div>
  );
};

export default Community;
